// Person reference input — name + email + per-person alerts toggle, with
// optional "Linked user" mode that swaps the name + email free-text inputs
// for a typeahead picker over org users.
//
// Required-field semantics, per the backend ContractPeopleSchema invariants
// (backend/src/models/managedContract.ts):
//   - The contract must have exactly one owner and exactly one manager.
//     Callers that surface the two canonical roles pass `nameRequired` so
//     the form blocks submission on empty name fields at the HTML5 layer.
//   - Manager.alertsEnabled is server-enforced to true — pass `alertsLocked`
//     to render the alerts toggle forced-on and disabled.
//   - Email is required whenever alertsEnabled is true (server rejects
//     alertsEnabled=true with no email). The "Email *" placeholder +
//     `required` attribute fire as soon as the alerts toggle is on,
//     regardless of whether the name has been entered yet.
//
// Linked-user mode:
//   - Enabled by passing `availableUsers`. The input renders a mode toggle
//     ("External contact" vs "Linked user"). In linked mode the picker
//     populates name + email + userId atomically, and the name/email
//     inputs become read-only display rows so the operator can't drift
//     the linked user's display name on this contract.

import { useState } from "react";
import LinkedUserPicker from "./LinkedUserPicker";
import type { OrganisationUser, PersonReference } from "../../types";

interface PersonReferenceInputProps {
  label: string;
  value?: PersonReference;
  onChange: (value: PersonReference | undefined) => void;
  disabled?: boolean;
  /** When true, the alerts toggle is forced on and disabled — used for
   *  the manager role, whose alertsEnabled is server-enforced. */
  alertsLocked?: boolean;
  /** When true, the name input carries `required` so HTML5 form
   *  validation blocks submit on empty. Used by callers like
   *  ManagedContractForm where the role is canonical and must be present. */
  nameRequired?: boolean;
  /** When provided, enables linked-user mode. The mode toggle appears at
   *  the top of the input; selecting a user from the picker hydrates the
   *  PersonReference's name + email + userId. */
  availableUsers?: OrganisationUser[];
}

type Mode = "external" | "linked";

export default function PersonReferenceInput({
  label,
  value,
  onChange,
  disabled,
  alertsLocked,
  nameRequired,
  availableUsers,
}: PersonReferenceInputProps) {
  const alertsEnabled = alertsLocked ? true : value?.alertsEnabled ?? true;
  // Email is required whenever alerts are on. The visible "*" + `required`
  // attribute fire from page load (not just after the user types a name)
  // so the form communicates upfront what the schema demands.
  const emailRequired = alertsEnabled;

  // The initial mode mirrors whether the existing value already carries a
  // userId — preserves "linked" state on edit. Default to external for new
  // entries because that's still the more common shape on first contract
  // capture.
  const [mode, setMode] = useState<Mode>(value?.userId ? "linked" : "external");
  // Mode toggle only appears when the caller passes a non-empty user
  // list — otherwise there's nothing to link to.
  const linkedAvailable = !!availableUsers && availableUsers.length > 0;
  const linkedMode = linkedAvailable && mode === "linked";

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (!name && !value?.email) {
      onChange(undefined);
    } else {
      onChange({ name, email: value?.email, alertsEnabled, userId: value?.userId });
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    onChange({
      name: value?.name || "",
      email: email || undefined,
      alertsEnabled,
      userId: value?.userId,
    });
  };

  const handleAlertsToggle = () => {
    if (alertsLocked) return;
    const next = !alertsEnabled;
    onChange({
      name: value?.name || "",
      email: value?.email,
      alertsEnabled: next,
      userId: value?.userId,
    });
  };

  const handleLinkedSelect = (u: OrganisationUser | undefined) => {
    if (!u) {
      // Clearing the link reverts to a blank free-text entry so the
      // operator can either pick a new user or fall back to external.
      onChange(undefined);
      return;
    }
    onChange({
      name: u.name,
      email: u.email,
      alertsEnabled,
      userId: u.userId,
    });
  };

  const handleModeChange = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    if (next === "external") {
      // Drop the linked userId; keep the name/email so the operator can
      // tweak them as a free-text entry rather than starting from scratch.
      if (value?.userId) {
        onChange({
          name: value.name,
          email: value.email,
          alertsEnabled,
        });
      }
    } else {
      // Switching INTO linked mode without a selection clears the
      // free-text values so the picker isn't polluted with stale data.
      onChange(undefined);
    }
  };

  const namePlaceholder = nameRequired ? "Name *" : "Name";
  const emailPlaceholder = emailRequired ? "Email *" : "Email (optional)";

  return (
    <fieldset className="space-y-2">
      <legend className="label">{label}</legend>
      {linkedAvailable && (
        <div role="radiogroup" aria-label={`${label} entry type`} className="flex items-center gap-3 text-caption">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name={`${label}-mode`}
              value="external"
              checked={mode === "external"}
              onChange={() => handleModeChange("external")}
              disabled={disabled}
              className="accent-brand-600"
            />
            <span className="text-slate-700 dark:text-slate-300">External contact</span>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name={`${label}-mode`}
              value="linked"
              checked={mode === "linked"}
              onChange={() => handleModeChange("linked")}
              disabled={disabled}
              className="accent-brand-600"
            />
            <span className="text-slate-700 dark:text-slate-300">Linked user</span>
          </label>
        </div>
      )}
      {linkedMode ? (
        <>
          <LinkedUserPicker
            users={availableUsers!}
            value={value?.userId}
            onSelect={handleLinkedSelect}
            disabled={disabled}
            label={label}
          />
          {/* Render the linked name + email read-only so they're still
              visible alongside the alerts toggle; the picker chip already
              shows them, but the explicit row keeps the column layout
              parity across modes. */}
        </>
      ) : (
        <>
          <input
            type="text"
            placeholder={namePlaceholder}
            value={value?.name || ""}
            onChange={handleNameChange}
            className="input"
            disabled={disabled}
            required={nameRequired}
            aria-required={nameRequired}
            aria-label={`${label} name`}
          />
          <input
            type="email"
            placeholder={emailPlaceholder}
            value={value?.email || ""}
            onChange={handleEmailChange}
            className="input"
            disabled={disabled}
            required={emailRequired}
            aria-required={emailRequired}
            aria-label={`${label} email`}
          />
        </>
      )}
      <label className="flex items-center gap-2 text-body-sm">
        <button
          type="button"
          role="switch"
          aria-checked={alertsEnabled}
          aria-label={`${label} alerts enabled`}
          onClick={handleAlertsToggle}
          disabled={disabled || alertsLocked}
          className={
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors " +
            (alertsEnabled ? "bg-brand-500" : "bg-slate-300 dark:bg-slate-600") +
            (alertsLocked ? " opacity-60 cursor-not-allowed" : "")
          }
        >
          <span
            className={
              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform " +
              (alertsEnabled ? "translate-x-4" : "translate-x-0.5")
            }
          />
        </button>
        <span className="text-slate-700 dark:text-slate-300">
          Send overdue &amp; due-soon alerts
        </span>
      </label>
      {alertsLocked && (
        <p className="text-caption text-slate-500 dark:text-slate-400">
          Required for managers — alerts cannot be disabled.
        </p>
      )}
    </fieldset>
  );
}
